import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Box } from "@chakra-ui/react";

// =============================================================
// Ô vẽ chữ kí cho cổng nhân viên (web).
// Khớp cấu hình của bản web frontend-v2 (signature_pad): nét màu #0057B8,
// độ dày cố định 1, bitmap canvas nền TRONG SUỐT để BE sharp().trim()
// cắt sát nét bút -> PNG RGBA giống hệt app.
// =============================================================

const SIGNATURE_BLUE = "#0057B8";
const LINE_WIDTH = 1;
const BORDER = "#E1E1E1";

const SignaturePad = forwardRef(({ height = 220 }, ref) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  // Dựng lại bitmap theo bề rộng thật + devicePixelRatio (giống resizeCanvas của FEv2)
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const displayWidth = container.offsetWidth;
    if (!displayWidth) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${height}px`;
    canvas.width = displayWidth * ratio;
    canvas.height = height * ratio;

    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = SIGNATURE_BLUE;
    ctxRef.current = ctx;

    // Đổi kích thước bitmap sẽ xóa nội dung -> coi như chưa vẽ
    emptyRef.current = true;
  }, [height]);

  useEffect(() => {
    const container = canvasRef.current?.parentElement;
    if (!container) return undefined;

    resizeCanvas();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(container);
    return () => observer.disconnect();
  }, [resizeCanvas]);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Chấm nhỏ để chạm 1 phát cũng thành nét (giống signature_pad)
    ctx.lineTo(x, y);
    ctx.stroke();
    emptyRef.current = false;
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    e.preventDefault();
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endStroke = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
    ctxRef.current?.closePath();
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    emptyRef.current = true;
  }, []);

  useImperativeHandle(ref, () => ({
    isEmpty: () => emptyRef.current,
    /** Trả về data-uri PNG (nền trong suốt), hoặc null nếu chưa vẽ gì. */
    getSignature: () =>
      emptyRef.current ? null : canvasRef.current?.toDataURL("image/png") || null,
    clear,
  }));

  return (
    <Box w="100%" position="relative">
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerLeave={endStroke}
        onPointerCancel={endStroke}
        style={{
          display: "block",
          width: "100%",
          height: `${height}px`,
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          background: "#fff",
          // Không để trình duyệt cuộn/zoom khi đang vẽ trên mobile
          touchAction: "none",
          cursor: "crosshair",
        }}
      />
    </Box>
  );
});

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
